import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { ProductionGroupSelection } from '@paragon/production-app-utilities';

import {
  createProductionComponentInstancesFromQuantityMap,
  loadProductionGroupData,
} from '../../store/actions/productionGroups';
import { getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { getAnalysisSetGuidsByProductionGroupGuid } from '../../store/selectors/getAnalysisSetGuidsByProductionGroupGuid';
import { getComponentDesignGuidToMostRecentAnalysisSetGuidMap } from '../../store/selectors/getComponentDesignGuidToMostRecentAnalysisSetGuidMap';
import { getDoWorkingChangesExistByLoadedComponentDesignGuid } from '../../store/selectors/getDoWorkingChangesExistByComponentDesignGuid';
import { getProductionGroupsByProjectGuid } from '../../store/selectors/getProductionGroupsByProjectGuid';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ChooseQuantities, useQuantitiesAndOptionState } from './ChooseQuantities';

export function AddToProductionGroupDialog({ scope }: { readonly scope: ProjectActionScope }) {
  const dispatch = useAppDispatch();
  const layoutModels = useAppSelector(getLayoutModelsWithPendingChanges);
  const analysisHistory = useAppSelector((state) => state.analysisHistory);
  const analysisSetGuidsByProductionGroupGuid = useAppSelector(getAnalysisSetGuidsByProductionGroupGuid);
  const componentDesignGuidToMostRecentAnalysisSetGuidMap = useAppSelector(
    getComponentDesignGuidToMostRecentAnalysisSetGuidMap,
  );
  const productionGroupsByProjectGuid = useAppSelector(getProductionGroupsByProjectGuid);

  const doWorkingChangesExistByComponentDesignGuid = useAppSelector(
    getDoWorkingChangesExistByLoadedComponentDesignGuid,
  );

  const projectGuid = getScopeProjectGuids(scope)[0];

  const [activeProductionGroupGuid, setActiveProductionGroupGuid] = useState<string | null>(null);
  const [quantityValues, setQuantityValues, quantityOption, setQuantityOption] = useQuantitiesAndOptionState(scope);

  function updateActiveProductionGroupGuid(productionGroupGuid: string | null) {
    // When selecting a production group, load its production component instances so that we can compare them against
    // the trusses that are being added.
    if (productionGroupGuid !== null) {
      dispatch(loadProductionGroupData(productionGroupGuid));
    }
    setActiveProductionGroupGuid(productionGroupGuid);
  }

  const componentsThatAlreadyExistInProductionGroup: string[] = useMemo(() => {
    if (activeProductionGroupGuid === null) return [];
    const analysisSetGuidsForActiveProductionGroup = analysisSetGuidsByProductionGroupGuid[activeProductionGroupGuid];
    if (!analysisSetGuidsForActiveProductionGroup) return [];

    const existingComponents: string[] = [];
    for (const componentDesignGuid of Object.keys(quantityValues)) {
      const componentDesignHistory = analysisHistory[componentDesignGuid];
      if (componentDesignHistory == null) continue;
      const analysisSetGuidsForComponent = Object.keys(componentDesignHistory);
      if (
        analysisSetGuidsForComponent.some((analysisSetGuidForComponentToAdd) =>
          analysisSetGuidsForActiveProductionGroup.includes(analysisSetGuidForComponentToAdd),
        )
      ) {
        const mostRecentAnalysisSetGuid = componentDesignGuidToMostRecentAnalysisSetGuidMap[componentDesignGuid];
        if (!mostRecentAnalysisSetGuid) {
          existingComponents.push('Unknown');
          continue;
        }
        existingComponents.push(layoutModels[componentDesignGuid].name);
      }
    }
    return existingComponents;
  }, [
    activeProductionGroupGuid,
    analysisSetGuidsByProductionGroupGuid,
    componentDesignGuidToMostRecentAnalysisSetGuidMap,
    analysisHistory,
    quantityValues,
    layoutModels,
  ]);

  const componentDesignGuidsWithWorkingChanges = useMemo(
    () =>
      Object.entries(quantityValues)
        .filter(
          ([componentDesignGuid, quantity]) =>
            quantity > 0 && doWorkingChangesExistByComponentDesignGuid[componentDesignGuid],
        )
        .map(([componentDesignGuid]) => componentDesignGuid),
    [quantityValues, doWorkingChangesExistByComponentDesignGuid],
  );

  return (
    <Dialog
      title="Add to Production Group"
      open
      submitText="Add"
      onSubmit={() => {
        dispatch(createProductionComponentInstancesFromQuantityMap(quantityValues, activeProductionGroupGuid!));
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '400px' }}
      submitDisabled={
        activeProductionGroupGuid === null ||
        componentsThatAlreadyExistInProductionGroup.length > 0 ||
        componentDesignGuidsWithWorkingChanges.length > 0
      }
      enterKeySubmits
    >
      <Box sx={{ my: 2 }}>
        <ProductionGroupSelection
          activeProductionGroupGuid={activeProductionGroupGuid}
          setActiveProductionGroupGuid={updateActiveProductionGroupGuid}
          productionGroups={productionGroupsByProjectGuid[projectGuid]}
        />
      </Box>
      {componentsThatAlreadyExistInProductionGroup.length > 0 ? (
        <InlineNotification type="error">
          Cannot add to this production group because these components already exist there:{' '}
          {componentsThatAlreadyExistInProductionGroup.join(', ')}
        </InlineNotification>
      ) : null}
      {componentDesignGuidsWithWorkingChanges.length > 0 ? (
        <InlineNotification type="error">
          Components have working changes:{' '}
          {componentDesignGuidsWithWorkingChanges
            .map((componentDesignGuid) => layoutModels[componentDesignGuid].name)
            .join(', ')}
          . Analyze them first, then return here and refresh.
        </InlineNotification>
      ) : null}
      <Typography>Set quantities:</Typography>
      <ChooseQuantities
        scope={scope}
        quantityOption={quantityOption}
        quantities={quantityValues}
        onQuantityChange={(quantityOption, quantities) => {
          setQuantityOption(quantityOption);
          setQuantityValues(quantities);
        }}
      />
    </Dialog>
  );
}
